import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useCallback } from 'react'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { NavCategoryRow } from '@src/components/widgets/navigation/nav-category-row'
import type { NavCategory } from '@src/components/widgets/navigation/types'
import { setFindingMethod, FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_ACIONS, TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackClickEvent } from '@src/telemetry/track'

import type { HeaderLinkScrollingMenuWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * HEADER_LINK_SCROLLING_MENU widget (DS path).
 *
 * Renders the BE-driven quick-link menu through the DS header's own mobile nav row
 * (`NavCategoryRow` — the DS equivalent of legacy's tertiary-menu strip), so the widget is
 * pixel-identical to the row the DS header stops rendering on the PL home page: same pill items,
 * DS carousel band, and top hairline. Visibility is gated by the same `base`/`lg` breakpoint as
 * the in-header row wrapper it replaces (NOT the UA-derived deviceType, which would leave tablets
 * and sub-1200px desktop windows with no row at all): below `lg` this widget shows, at `lg`+ it
 * hides and the DS header's own row shows the primary taxonomy instead. Telemetry mirrors the DS
 * nav's quick-link row
 * (`click_header_quick_link` + the NAVIGATION finding-method — see `handleCategoryLinkClick` in
 * navigation/index.tsx).
 */
export function HeaderLinkScrollingMenuDSWidget(props: PLTypedWidgetProps<HeaderLinkScrollingMenuWidgetData>) {
  const { t } = useTranslation('common')
  const data = props.data.headerLinkScrollingMenuData
  const items = data?.items ?? []

  const handleCategoryLinkClick = useCallback(
    (category: NavCategory, index: number) => {
      setFindingMethod[FINDING_METHOD_AGGREGATE.NAVIGATION]?.({
        L1: category.label,
      })
      trackClickEvent(TELEMETRY_EVENT_NAMES.BUTTON_CLICK, {
        trackGA: true,
        trackInternal: true,
        category: t('navigation'),
        action: TELEMETRY_EVENT_ACIONS.CLICK_HEADER_QUICK_LINK,
        value: index,
        label: category.label,
      })
    },
    [t],
  )

  if (!data?.enabled || items.length === 0) return null

  // LinkItem → NavCategory, mirroring adaptContentfulMenusToNavCategories: a colored item maps to
  // the `highlight` (cantaloupe) pill variant — DS renders the token color, not the raw wire hex.
  const categories: NavCategory[] = items.map(item => ({
    id: item.id || item.slug,
    label: item.text,
    href: item.slug,
    ...(item.textColor ? { highlight: true } : {}),
  }))

  return (
    // Same root locator as the legacy branch for QA parity. The display gate matches the DS
    // header's row-reveal wrapper (base visible / hidden from `lg`) so the widget's visibility and
    // the header's row suppression are keyed off the same breakpoint axis.
    <div data-testid="header-link-scrolling-menu-widget" className={css({ display: { base: 'block', lg: 'none' } })}>
      <NavCategoryRow categories={categories} onCategoryLinkClick={handleCategoryLinkClick} isMobile />
    </div>
  )
}
